import { lazy, Suspense, useContext, useEffect, useRef, useState } from "react"
import Map from "@arcgis/core/Map"
import SceneView from "@arcgis/core/views/SceneView"
import type { GoToTarget3D } from "@arcgis/core/views/types.js"
// @ts-ignore
import "../map.css"
import { Grid2 as Grid, IconButton, Popper, Tooltip } from "@mui/material"
import { pointsLayer, pointsOfInterestLayer, createRiverLayer, applyRiverFloatability, floatabilityColors, selectionLayer, setView, setMap, map, view, pointOfInterestTypes, setVisiblePointOfInterestTypes } from "../utilities/buffaloArcGisHelper"
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils.js"
import NavigationIcon from '@mui/icons-material/Navigation'
import LegendToggleIcon from '@mui/icons-material/LegendToggle'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import { rootContext } from "../contexts/rootContext"
import { useNavigate, useSearchParams } from "react-router"
import SupaLink from "../components/SupaLink"
import SupaLinkDialog from "../components/SupaLinkDialog"
import { AboutMap } from "../components/buffalo/AboutMap"
import { Warning } from "../components/buffalo/Warning"
import { TripDiv } from "../components/buffalo/TripDiv"
import { PointOfInterestDiv } from "../components/buffalo/PointOfInterestDiv"
import { TripDetails } from "../components/buffalo/TripDetails"
import { localStorageNames, lsFalse, lsTrue } from "../utilities/constants"
import { TimeSlider } from "../components/buffalo/TimeSlider"
import { getTimeStringsAndFloatabilityScores } from "../utilities/buffaloDataHelper"
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import SatelliteAltOutlinedIcon from '@mui/icons-material/SatelliteAltOutlined'
import { isDebugEnabled } from "../utilities/debug"
import KayakingRoundedIcon from '@mui/icons-material/KayakingRounded';
import { PointsOfInterestMenu } from "../components/buffalo/PointsOfInterestMenu"
import type { PoiType } from "../data/riverPointsOfInterest"

// Loaded through a dynamic import so the debug overlay is split into its own
// chunk. It instantiates arcgis objects when its module evaluates, which is a
// side effect webpack will not tree shake, so a static import would leave the
// whole overlay sitting in the main bundle of a production build even though
// isDebugEnabled never lets it render. This way the chunk is never requested.
const DebugCoordinatesDiv = lazy(() =>
    import("../components/buffalo/DebugCoordinatesDiv")
        .then((module) => ({ default: module.DebugCoordinatesDiv }))
)
const DebugDataWindowDiv = lazy(() =>
    import("../components/buffalo/DebugDataWindowDiv")
        .then((module) => ({ default: module.DebugDataWindowDiv }))
)

// osm-3d is one of the 3d basemaps and satellite is the 2d world imagery
// service. Both render in a SceneView, so switching is just a reassignment.
const getBasemapId = (useSatellite: boolean) => useSatellite ? "satellite" : "osm-3d"

// Points of interest are hidden by default. Before types could be picked one at
// a time this was a single on/off setting, which carries over as all or none.
const getSavedVisiblePointOfInterestTypes = (): PoiType[] => {
    const savedValue = localStorage.getItem(localStorageNames.visiblePointOfInterestTypes)
    if (savedValue === null)
        return localStorage.getItem(localStorageNames.showPointsOfInterest) === lsTrue ? [...pointOfInterestTypes] : []
    try {
        const savedTypes: unknown = JSON.parse(savedValue)
        return Array.isArray(savedTypes)
            ? pointOfInterestTypes.filter((type) => savedTypes.includes(type))
            : []
    } catch {
        return []
    }
}

function Buffalo() {
    const context = useContext(rootContext)
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()
    const mapInitialized = useRef(false)
    const mapDiv = useRef<HTMLDivElement>(null)

    const tripDetailsAccessPoint1 = searchParams.get("tripFrom")
    const tripDetailsAccessPoint2 = searchParams.get("tripTo")
    const isTripDetailsOpen = tripDetailsAccessPoint1 !== null && tripDetailsAccessPoint2 !== null

    const [isLegendOpen, setIsLegendOpen] = useState(() => {
        const savedValue = localStorage.getItem(localStorageNames.showLegend)
        return savedValue !== lsFalse
    })
    const [showRiverAccessPoints, setShowRiverAccessPoints] = useState(() => {
        const savedValue = localStorage.getItem(localStorageNames.showAccessPoints)
        return savedValue !== lsFalse
    })
    const [visiblePointOfInterestTypes, setVisiblePointOfInterestTypesState] = useState(getSavedVisiblePointOfInterestTypes)
    const [pointsOfInterestMenuAnchor, setPointsOfInterestMenuAnchor] = useState<HTMLElement | null>(null)
    const showPointsOfInterest = visiblePointOfInterestTypes.length > 0
    const [isTimeSliderOpen, setIsTimeSliderOpen] = useState(() => {
        const savedValue = localStorage.getItem(localStorageNames.showTimeSlider)
        return savedValue !== lsFalse
    })
    const [useSatelliteBasemap, setUseSatelliteBasemap] = useState(() => {
        const savedValue = localStorage.getItem(localStorageNames.useSatelliteBasemap)
        return savedValue === lsTrue
    })
    const [cameraHeading, setCameraHeading] = useState(0)
    const [isDialogOpen, setIsDialogOpen] = useState(false)
    const [dialogTitle, setDialogTitle] = useState("")
    const [dialogContent, setDialogContent] = useState(<></>)
    const [showX, setShowX] = useState(true)

    const [timeStrings, setTimeStrings] = useState<string[]>([])
    const [polylineFloatabilityByTimeStringIndex, setPolylineFloatabilityByTimeStringIndex] = useState<number[][]>([])

    useEffect(()=>{
        const ack = localStorage.getItem("ack")
        if (ack === null) {
            setDialogTitle("Warning")
            setDialogContent(<Warning 
                onAcknowledge={()=>{
                    setIsDialogOpen(false)
                    localStorage.setItem("ack", "true")
                }}
            />)
            setShowX(false)
            setIsDialogOpen(true)
        }
    }, [])

    useEffect(() => {
        initMap()
    }, [mapDiv])

    // Trip details covers the map and can also be opened from the url or the
    // browser history, so map only popups are closed off its state rather than
    // the button that usually opens it.
    useEffect(() => {
        if (isTripDetailsOpen)
            setPointsOfInterestMenuAnchor(null)
    }, [isTripDetailsOpen])

    const handleLegendClick = (_event: React.MouseEvent<HTMLElement>) => {
        localStorage.setItem(localStorageNames.showLegend, isLegendOpen ? lsFalse : lsTrue)
        setIsLegendOpen(prev => !prev)
    }

    const handleTimeSliderToggleClick = (_event: React.MouseEvent<HTMLElement>) => {
        localStorage.setItem(localStorageNames.showTimeSlider, isTimeSliderOpen ? lsFalse : lsTrue)
        setIsTimeSliderOpen(prev => !prev)
    }

    const handleOpenTripDetails = (accessPoint1: string, accessPoint2: string) => {
        setSearchParams({ tripFrom: accessPoint1, tripTo: accessPoint2 })
    }

    const handleCloseTripDetails = () => {
        setSearchParams({})
    }

    const handleShowAccessPointsClick = () => {
        const showRiverAccessPointsNew = !showRiverAccessPoints
        if (showRiverAccessPointsNew)
            map?.add(pointsLayer)
        else
            map?.remove(pointsLayer)
        localStorage.setItem(localStorageNames.showAccessPoints, showRiverAccessPointsNew ? lsTrue : lsFalse)
        setShowRiverAccessPoints(showRiverAccessPointsNew)
    }

    const handlePointsOfInterestClick = (event: React.MouseEvent<HTMLElement>) => {
        setPointsOfInterestMenuAnchor(event.currentTarget)
    }

    const handleVisiblePointOfInterestTypesChange = (types: PoiType[]) => {
        setVisiblePointOfInterestTypes(types)
        localStorage.setItem(localStorageNames.visiblePointOfInterestTypes, JSON.stringify(types))
        setVisiblePointOfInterestTypesState(types)
    }

    const handleBasemapClick = () => {
        const useSatelliteBasemapNew = !useSatelliteBasemap
        if (map !== null)
            map.basemap = getBasemapId(useSatelliteBasemapNew)
        localStorage.setItem(localStorageNames.useSatelliteBasemap, useSatelliteBasemapNew ? lsTrue : lsFalse)
        setUseSatelliteBasemap(useSatelliteBasemapNew)
    }

    const handleCompassClick = () => {
        // Same call the arcgis Compass widget makes. The 5.0 typings for goTo
        // dropped heading, but it still rotates around the view center at runtime.
        view?.goTo({ heading: 0 } as GoToTarget3D)
    }

    // Debug only: refetch the sensor window ending at windowEnd (null = now)
    // and repaint the river at the newest reading in it. The time slider resets
    // itself to that reading when timeStrings changes.
    const handleDebugLoadWindow = async (windowEnd: Date | null) => {
        const result = await getTimeStringsAndFloatabilityScores(windowEnd ?? new Date())
        const latestPolylineFloatability = result.polylineFloatabilityByTimeStringIndex[result.polylineFloatabilityByTimeStringIndex.length-1]
        await applyRiverFloatability(latestPolylineFloatability)
        setTimeStrings(result.timeStrings)
        setPolylineFloatabilityByTimeStringIndex(result.polylineFloatabilityByTimeStringIndex)
    }

    async function initMap() {
        if (mapDiv.current == null && !mapInitialized.current)
            return
        else
            mapInitialized.current = true

        const {timeStrings, polylineFloatabilityByTimeStringIndex } = await getTimeStringsAndFloatabilityScores()
        const latestPolylineFloatability = polylineFloatabilityByTimeStringIndex[polylineFloatabilityByTimeStringIndex.length-1]
        setTimeStrings(timeStrings)
        setPolylineFloatabilityByTimeStringIndex(polylineFloatabilityByTimeStringIndex)

        const newMap = new Map({
            basemap: getBasemapId(useSatelliteBasemap),
            ground: "world-elevation",
        })

        const sceneView = new SceneView({
            container: mapDiv.current,
            map: newMap,
            camera: {
                position: {
                    spatialReference: {
                    latestWkid: 3857,
                    wkid: 102100,
                    },
                    x: -10337508.556265375,
                    y: 4287942.169795147,
                    z: 250515.12605815567
                },
                heading: 0,
                tilt: 0,
            }
        })

        newMap.add(selectionLayer)
        newMap.add(createRiverLayer(latestPolylineFloatability))
        setVisiblePointOfInterestTypes(visiblePointOfInterestTypes)
        newMap.add(pointsOfInterestLayer)
        if (showRiverAccessPoints)
            newMap.add(pointsLayer)
        setMap(newMap)

        sceneView.ui.empty("top-left")
        reactiveUtils.watch(
            () => sceneView.camera?.heading,
            (heading) => setCameraHeading(heading ?? 0),
            { initial: true }
        )

        setView(sceneView)
    }

    return (<>
        <Grid 
            container 
            sx={{ paddingTop: "5px", paddingRight: "5px", paddingLeft: "5px" }} 
            spacing={0} 
            size={12}
        >
            <IconButton
                onClick={()=>{navigate("/")}}
                sx={{
                    cursor: `url(${context.theme.cursor.pointer}), pointer`,
                    "&:hover": {
                        backgroundColor: context.theme.misc.buttonHover
                    },
                    marginRight: "10px"
                }}
                aria-label="navigate home"
            >
                <img
                    src={context.theme.icons.cicada}
                    height="32px"
                    width="32px"
                    alt="cicada logo"
                />
            </IconButton>
            <SupaLink
                text="About Map"
                handleClick={()=> {
                    setDialogContent(<AboutMap />)
                    setDialogTitle("About Map")
                    setIsDialogOpen(true)
                    setShowX(true)
                }}
                style={{ marginRight: "20px" }}
            />
        </Grid>
        <Grid size="grow" sx={{paddingTop: "5px"}}>
            <div
                ref={mapDiv}
                style={{
                    padding: "0px",
                    margin: "0px",
                    width: "100%",
                    height: "100%"
                }} 
            />
        </Grid>
        <Tooltip title="Reset to North" placement="right">
            <IconButton
                onClick={handleCompassClick}
                sx={{
                    backgroundColor: "#242424",
                    position: "absolute",
                    top: "70px",
                    left: "15px",
                    "&:hover": {
                        backgroundColor: "#4a4a4a"
                    }
                }}
                size="small"
                aria-label="reset to north"
            >
                {/* The needle points north, so it turns opposite to the camera heading. */}
                <NavigationIcon
                    sx={{
                        fill: "#9e9e9e",
                        transform: `rotate(${-cameraHeading}deg)`,
                        "&:hover": {
                            fill: "#FFFFFF"
                        }
                    }}
                />
            </IconButton>
        </Tooltip>
        <Tooltip title="River Access Points" placement="right">
            <IconButton
                onClick={handleShowAccessPointsClick}
                sx={{
                    backgroundColor: "#242424",
                    outline: showRiverAccessPoints ? "solid #009af2 2px" : "none",
                    position: "absolute",
                    top: "115px",
                    left: "15px",
                    "&:hover": {
                        backgroundColor: "#4a4a4a"
                    }
                }}
                size="small"
            >
                <KayakingRoundedIcon
                    sx={{
                        fill: showRiverAccessPoints ? "#009af2" : "#9e9e9e",
                        "&:hover": {
                            fill: showRiverAccessPoints ? "#009af2" : "#FFFFFF"
                        }
                    }}
                />
            </IconButton>
        </Tooltip>
        <Tooltip title="Points of Interest" placement="right">
            <IconButton
                onClick={handlePointsOfInterestClick}
                aria-label="choose points of interest"
                aria-haspopup="dialog"
                sx={{
                    backgroundColor: "#242424",
                    outline: showPointsOfInterest ? "solid #009af2 2px" : "none",
                    position: "absolute",
                    top: "160px",
                    left: "15px",
                    "&:hover": {
                        backgroundColor: "#4a4a4a"
                    }
                }}
                size="small"
            >
                <VisibilityOutlinedIcon
                    sx={{
                        fill: showPointsOfInterest ? "#009af2" : "#9e9e9e",
                        "&:hover": {
                            fill: showPointsOfInterest ? "#009af2" : "#FFFFFF"
                        }
                    }}
                />
            </IconButton>
        </Tooltip>
        <PointsOfInterestMenu
            anchorEl={pointsOfInterestMenuAnchor}
            onClose={() => setPointsOfInterestMenuAnchor(null)}
            visibleTypes={visiblePointOfInterestTypes}
            onChange={handleVisiblePointOfInterestTypesChange}
        />
        <Tooltip title="Legend" placement="right">
            <IconButton
                onClick={handleLegendClick}
                sx={{
                    backgroundColor: "#242424",
                    outline: isLegendOpen ? "solid #009af2 2px" : "none",
                    position: "absolute",
                    top: "205px",
                    left: "15px",
                    "&:hover": {
                        backgroundColor: "#4a4a4a"
                    }
                }}
                size="small"
            >
                <LegendToggleIcon
                    sx={{
                        fill: isLegendOpen ? "#009af2" : "#9e9e9e",
                        "&:hover": {
                            fill: isLegendOpen ? "#009af2" : "#FFFFFF"
                        }
                    }}
                />
            </IconButton>
        </Tooltip>
        <Tooltip title="Time Selector" placement="right">
            <IconButton
                onClick={handleTimeSliderToggleClick}
                sx={{
                    backgroundColor: "#242424",
                    outline: isTimeSliderOpen ? "solid #009af2 2px" : "none",
                    position: "absolute",
                    top: "250px",
                    left: "15px",
                    "&:hover": {
                        backgroundColor: "#4a4a4a"
                    }
                }}
                size="small"
            >
                <AccessTimeIcon
                    sx={{
                        fill: isTimeSliderOpen ? "#009af2" : "#9e9e9e",
                        "&:hover": {
                            fill: isTimeSliderOpen ? "#009af2" : "#FFFFFF"
                        }
                    }}
                />
            </IconButton>
        </Tooltip>
        <Tooltip title="Satellite Imagery" placement="right">
            <IconButton
                onClick={handleBasemapClick}
                sx={{
                    backgroundColor: "#242424",
                    outline: useSatelliteBasemap ? "solid #009af2 2px" : "none",
                    position: "absolute",
                    top: "295px",
                    left: "15px",
                    "&:hover": {
                        backgroundColor: "#4a4a4a"
                    }
                }}
                size="small"
            >
                <SatelliteAltOutlinedIcon
                    sx={{
                        fill: useSatelliteBasemap ? "#009af2" : "#9e9e9e",
                        "&:hover": {
                            fill: useSatelliteBasemap ? "#009af2" : "#FFFFFF"
                        }
                    }}
                />
            </IconButton>
        </Tooltip>
        {/* Hidden rather than closed, since the legend is a saved preference that
            should be back when returning to the map. */}
        <Popper
            open={isLegendOpen && !isTripDetailsOpen}
        >
            <Grid 
                container 
                sx={{ 
                    position: "absolute",
                    top: "70px",
                    left:"60px",
                    backgroundColor: "black",
                    color: "white",
                    padding: "10px",
                    borderRadius: "15px",
                    width: "220px",
                    userSelect: "none"
                }}
                spacing={1}
            >
                <Grid size={12} sx={{ fontWeight: "bold" }}>
                    Estimated Level
                </Grid>
                <Grid size={12} container spacing={1} >
                    <Grid size={3} style={{
                        height: "auto",
                        backgroundImage: `linear-gradient(to top, ${floatabilityColors.veryLow}, ${floatabilityColors.low}, ${floatabilityColors.moderate}, ${floatabilityColors.high}, ${floatabilityColors.flood})`
                    }}/>
                    <Grid size={3} container spacing={0}>
                        <Grid size={12} sx={{ backgroundColor: floatabilityColors.flood }}/>
                        <Grid size={12} sx={{ backgroundColor: floatabilityColors.high }}/>
                        <Grid size={12} sx={{ backgroundColor: floatabilityColors.moderate }}/>
                        <Grid size={12} sx={{ backgroundColor: floatabilityColors.low }}/>
                        <Grid size={12} sx={{ backgroundColor: floatabilityColors.veryLow }}/>
                    </Grid>
                    <Grid container spacing={1} size={6}>
                        <Grid size={12}>Flood</Grid>
                        <Grid size={12}>High</Grid>
                        <Grid size={12}>Moderate</Grid>
                        <Grid size={12}>Low</Grid>
                        <Grid size={12}>Very Low</Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Popper>
        <TripDiv isTimeSliderOpen={isTimeSliderOpen} isTripDetailsOpen={isTripDetailsOpen} onOpenTripDetails={handleOpenTripDetails} />
        <PointOfInterestDiv isTripDetailsOpen={isTripDetailsOpen} />
        <TimeSlider
            open={isTimeSliderOpen}
            timeStrings={timeStrings}
            polylineFloatabilityByTimeStringIndex={polylineFloatabilityByTimeStringIndex}
        />
        {isTripDetailsOpen &&
            <TripDetails
                accessPoint1={tripDetailsAccessPoint1}
                accessPoint2={tripDetailsAccessPoint2}
                latestPolylineFloatability={polylineFloatabilityByTimeStringIndex[polylineFloatabilityByTimeStringIndex.length - 1] ?? []}
                latestReadingTime={timeStrings[timeStrings.length - 1]}
                onBack={handleCloseTripDetails}
            />
        }
        {isDebugEnabled &&
            <Suspense fallback={null}>
                <DebugCoordinatesDiv />
                <DebugDataWindowDiv onLoadWindow={handleDebugLoadWindow} />
            </Suspense>
        }
        <SupaLinkDialog 
            open={isDialogOpen}
            setOpen={setIsDialogOpen}
            title={dialogTitle}
            titleColor={context.theme.text.contrast2}
            content={dialogContent}
            showX={showX}
        />
    </>)
}

export default Buffalo